import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';

import { Edit } from 'styled-icons/boxicons-regular/Edit';
import { CheckCircle } from 'styled-icons/boxicons-regular/CheckCircle';
import styled from 'styled-components';
import COLOR from 'constants/colors';
import { studentCredentialLabel } from 'constants/constants';
import { patch as editCredentials } from 'actions/student-credentials';
import { Separator, Text, FlexView } from '@cuemath/comps';

import { userNameUpdates, getOnChangeMethod, getInputChangeMethod } from 'constants/form';

import { EVENT_TYPE } from 'constants/logger';
import { trackEvent } from 'modules/analytics';
import { FieldWrapper, renderFormField, CenterButton, Head } from 'modules/form';
import { Link } from 'react-router-dom';

const FlexWrapper = styled(FlexView)`
  margin: 15px 60px 15px 40px;
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const EditIcon = styled(Edit)`
  cursor: pointer;
`;

const CheckIcon = styled(CheckCircle)`
  margin: 40px auto;
`;

const getFormDefaults = () => {
  const formDefaults = {};

  userNameUpdates.forEach(field => {
    formDefaults[field.name] = field.default;
  });

  return formDefaults;
};

class EditStudentPasswordContainer extends PureComponent {
  static propTypes = {
    actions: PropTypes.shape({
      editCredentials: PropTypes.func.isRequired,
    }).isRequired,
    onUpdate: PropTypes.func.isRequired,
    student: PropTypes.shape({}).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isValid: true,
      errorText: null,
      message: null,
      credsPatch: store.patch(),
      form: getFormDefaults(),
      usernameFormFields: [...userNameUpdates],
      editUsername: false,
      credsUpdated: false,
      patchActionCalled: false,
    };

    [...userNameUpdates].forEach(field => {
      const methodName = getOnChangeMethod(field.name);
      this[methodName] = this.onChange.bind(this, field.name);
    });
  }

  static getDerivedStateFromProps(props, state) {
    const { onUpdate, studentCredentials } = props;
    const { credsPatch, patchActionCalled } = state;
    const studentCredentialsPatch = store.patch(studentCredentials, credsPatch.id);
    const { resolved } = studentCredentialsPatch || {};
    const { isPatched, isPatchingFailed } = resolved || {};
    const newState = {};

    if (resolved && patchActionCalled) {
      if (isPatched) {
        newState.credsUpdated = true;
        onUpdate();
      } else if (isPatchingFailed) {
        if (store.getError(resolved)) {
          newState.errorText = store.getError(resolved).message;
        } else {
          newState.errorText = 'Error';
        }
      }
      newState.patchActionCalled = false;
    }

    return newState;
  }

  onChange = (key, value) => {
    const { form } = this.state;
    const stateChanges = {
      form: {
        ...form,
        [key]: value,
      },
    };

    this.setState(() => ({ ...stateChanges }));
    this.clearMesssage();
  };

  clearMesssage = () => {
    const { errorText, message } = this.state;
    const stateChanges = {};

    if (errorText) stateChanges.errorText = null;
    if (message) stateChanges.message = null;
    this.setState(() => ({ ...stateChanges }));
  };

  onSubmit = event => {
    event.preventDefault();
    this.handlePatchAction();
  };

  fieldCheckError = (data, confirmationData, label) => {
    if (data !== confirmationData) {
      this.setState({ errorText: `${label} & Confirm ${label} does'nt match` });
      return true;
    } else if (data.trim().length < 6) {
      this.setState({ errorText: `${label} length must be atleast 6 characters` });
      return true;
    }
    return false;
  };

  handlePatchAction = () => {
    const { actions, student } = this.props;
    const { id: studentId } = student || {};
    const {
      form: { username, confirmUsername },
      editUsername,
      credsPatch: { id },
    } = this.state;
    const data = {};

    if (!editUsername) return;

    const usernameCheck =
      editUsername &&
      this.fieldCheckError(username, confirmUsername, studentCredentialLabel.USERNAME);

    if (usernameCheck) return;

    if (username) data.username = username;

    trackEvent(EVENT_TYPE.EDIT_LEAP_USERNAME_SUBMIT_CLICKED, { student_id: studentId });
    if (Object.keys(data).length) {
      this.setState({ patchActionCalled: true }, () => {
        actions.editCredentials(id, studentId, data);
      });
    }
  };

  renderFormFooter = () => {
    return (
      <Wrapper>
        <CenterButton label="Update" />
      </Wrapper>
    );
  };

  onEdit = () => {
    const stateChanges = {};
    const { usernameFormFields } = this.state;

    stateChanges.editUsername = true;
    stateChanges.usernameFormFields = usernameFormFields.map(({ ...val }) => {
      val.editable = true;
      return val;
    });

    this.setState(() => ({ ...stateChanges }));
  };

  render() {
    const {
      errorText,
      editUsername,
      credsUpdated,
      usernameFormFields,
      form,
    } = this.state;
    const { student: { id } = {} } = this.props;

    return (
      <React.Fragment>
        <Head>
          <Text size="xlarge" align="center">
            Edit Leap Username
          </Text>
        </Head>
        <Separator transparent height="10" />

        {!credsUpdated && (
          <form onSubmit={this.onSubmit}>
            {errorText && (
              <Text color={COLOR.BITTERSWEET} align="center" weight="semibold">
                {errorText}
              </Text>
            )}
            <Separator transparent height="10" />
            <FlexWrapper justifyContent="space-between" flexDirection="row">
              <Text size="large" weight="semibold">
                Username:
              </Text>
              {!editUsername && (
                <EditIcon
                  size={25}
                  color={COLOR.BOSTON_BLUE}
                  onClick={() => this.onEdit()}
                />
              )}
            </FlexWrapper>
            <FlexWrapper justifyContent="space-between" flexDirection="row">
              <Link
                target="_blank"
                to={{
                  pathname: `/student-pii/${id}`,
                }}
              >
                For student username, click here.
              </Link>
            </FlexWrapper>
            <FieldWrapper>
              {usernameFormFields.map(field => {
                const { editable, required, label: placeholder } = field;

                return renderFormField({
                  ...field,
                  value: form[field.name],
                  onChange: this[getInputChangeMethod(field)],
                  required,
                  disabled: !editable,
                  placeholder,
                });
              })}
            </FieldWrapper>
            <Separator transparent height="30" />
            {this.renderFormFooter()}
          </form>
        )}
        {credsUpdated && (
          <React.Fragment>
            <CheckIcon size={200} color={COLOR.POSITIVE} />
            <Text align="center" color={COLOR.POSITIVE} size="medium" weight="semibold">
              Username Successfully Updated!
            </Text>
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }
}

export default connect(
  ({ studentCredentials }) => ({ studentCredentials }),
  dispatch => ({
    actions: bindActionCreators(
      {
        editCredentials,
      },
      dispatch,
    ),
  }),
)(EditStudentPasswordContainer);
